// Core packages
import { HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

// Third party packages
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { DocumentArchiveDocsService } from "src/app/modules/properties/document-archive-docs.service";
import { ApiResponse, ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { DocumentArchiveHistoricalCheck } from "src/app/shared/interfaces/document-archive-historical.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { isArchiveV3Live } from "src/app/shared/utils/archive-switch.utils";

@Injectable({
  providedIn: "root"
})
export class DocumentArchivesService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private documentArchiveDocsService: DocumentArchiveDocsService
  ) { }

  /**
   * Create multiple virtual documents
   *
   * @since 1.0.0
   *
   * @param documentArchive documentArchive data
   * @returns Observable<any>
   */
  createMultipleVirtualDocuments(
    documentArchiveId: string,
    data: any,
  ): Observable<any> {
    return this.apiService.apiCall("POST", `document-archives/${documentArchiveId}/create-multiple-virtual-docs`, data);
  }

  /**
   * Create multiple digital documents
   *
   * @since 1.0.0
   *
   * @param documentArchive documentArchive data
   * @returns Observable<any>
   */
  createMultipleDigitalDocuments(
    documentArchiveId: string,
    data: any,
  ): Observable<any> {
    return this.apiService.apiCall("POST", `document-archives/${documentArchiveId}/create-multiple-digital-docs`, data);
  }

  /**
   * Create multiple paper documents
   *
   * @since 1.0.0
   *
   * @param documentArchive documentArchive data
   * @returns Observable<any>
   */
  createMultiplePaperDocuments(
    documentArchiveId: string,
    data: any,
  ): Observable<any> {
    return this.apiService.apiCall("POST", `document-archives/${documentArchiveId}/create-multiple-paper-docs`, data);
  }


  /**
   * Get given document archive with documentArchive ID
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of the documentArchive to get
   */
  get(documentArchiveId: string): Observable<any> {
    return this.apiService
      .apiCall("GET", `document-archives/${documentArchiveId}`)
      .pipe(
        catchError(err => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Get given document archive with documentArchive ID per QRcode area
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of the documentArchive to get
   */
  getQrcode(documentArchiveId: string): Observable<any> {
    return this.apiService
      .apiCall("GET", `document-archives/qrcode/${documentArchiveId}`)
      .pipe(
        catchError(err => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Update single document
   *
   * @since 1.0.0
   *
   * @param documentArchive documentArchive data
   * @returns Observable<any>
   */
  updateSingleDoc(
    documentArchiveId: string,
    documentTypeRoute: string,
    documentId: string,
    data: any,
  ): Observable<any> {
    return this.apiService.apiCall("PATCH", `document-archives/${documentArchiveId}/${documentTypeRoute}/${documentId}`, data);
  }
  updateSingleDoc2(
    documentArchiveId: string,
    documentTypeRoute: string,
    docStructureId: string,
    data: any,
  ): Observable<any> {
    return this.apiService.apiCall("PATCH", `document-archives/${documentArchiveId}/update-single-doc/${documentTypeRoute}/${docStructureId}`, data);
  }

  deleteSingleDoc(
    documentArchiveId: string,
    documentTypeRoute: string,
    docStructureId: string,
    data: any,
  ): Observable<any> {
    if (docStructureId === '') {
      docStructureId = undefined;
    }
    return this.apiService.apiCall("DELETE", `document-archives/${documentArchiveId}/delete-single-doc/${documentTypeRoute}/${docStructureId}`, data);
  }

  /**
   * Expire contract
   *
   * @since 1.0.0
   *
   * @param documentArchive documentArchive data
   * @returns Observable<any>
   */
  deleteDocs(
    documentArchiveId: string,
    data: any,
  ): Observable<any> {
    return this.apiService.apiCall("DELETE", `document-archives/${documentArchiveId}/delete-docs`, data);
  }

  /**
   * Expire contract
   *
   * @since 1.0.0
   *
   * @param documentArchive documentArchive data
   * @returns Observable<any>
   */
  expireContract(
    documentArchiveId: string,
    data: any,
  ): Observable<any> {
    return this.apiService.apiCall("PATCH", `document-archives/${documentArchiveId}/expire-contract`, data);
  }

  /**
   * Cancel contract
   *
   * @since 1.0.0
   *
   * @param documentArchive documentArchive data
   * @returns Observable<any>
   */
  cancelContract(
    documentArchiveId: string,
    data: any,
  ): Observable<any> {
    return this.apiService.apiCall("PATCH", `document-archives/${documentArchiveId}/cancel-contract`, data);
  }

  /**
   * Disable CPI document
   *
   * @since 1.0.0
   *
   * @param documentArchive documentArchive data
   * @returns Observable<any>
   */
  disableDocument(
    documentArchiveId: string,
    data: any,
  ): Observable<any> {
    return this.apiService.apiCall("PATCH", `document-archives/${documentArchiveId}/disable-document`, data);
  }

  /**
   * Update multi document
   *
   * @since 1.0.0
   *
   * @param documentArchive documentArchive data
   * @returns Observable<any>
   */
  updateMultiDoc(
    documentArchiveId: string,
    data: any,
  ): Observable<any> {
    return this.apiService.apiCall("PATCH", `document-archives/${documentArchiveId}/insert-multi-docs`, data);
  }

  /**
   * Update multi document
   *
   * @since 1.0.0
   *
   * @param documentArchive documentArchive data
   * @returns Observable<any>
   */
  updateMultiPaperDoc(
    documentArchiveId: string,
    data: any,
  ): Observable<any> {
    return this.apiService.apiCall("PATCH", `document-archives/${documentArchiveId}/insert-multi-paper-docs`, data);
  }

  /**
   * Create a new condo structure
   *
   * @since 1.0.0
   *
   * @param documentArchive documentArchive data
   * @returns Observable<any>
   */
  create(propertyId: string): Observable<any> {
    const data = {
      property: propertyId,
    };
    return this.apiService.apiCall("POST", "document-archives", data);
  }

  /**
   * Get condo structures list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", "document-archives", params);
  }

  /**
   * Check there are new inserted docs: the alert of the Home and the «!» of the menu.
   *
   * With the switch of the 3.0.0 archive on (NEW-M16-SW) the count comes from the new archive,
   * `GET /document-archive-docs/historical-docs/check`: the legacy route reads the other archive and
   * answers 410 once it is switched off. Both answer `data.totalCount`, so the callers do not change.
   *
   * @since 1.0.0
   *
   */
  checkHistoricalDocs(
  ): Observable<ApiResponse<DocumentArchiveHistoricalCheck>> {
    if (isArchiveV3Live()) {
      return this.documentArchiveDocsService.historicalDocsCheck();
    }
    return this.apiService.apiCall("GET", "document-archives/check-historical-docs");
  }

  /**
   * Get condo structures list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getHistoricalDocs(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any,
    propertyId?: string,
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus, propertyId };
    if (search) {
      Object.keys(search).forEach(key => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall("GET", "document-archives/historical-docs", params);
  }

  /**
   * Get an exportation file of all properties
   *
   * @since 1.0.0
   */
  getExport(): Observable<any> {
    return this.apiService
      .apiCall("GET", "document-archives/export", {}, { responseType: "blob" })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Get an exportation file of historical documents
   *
   * @since 1.0.0
   */
  getHistoricalDocsExport(): Observable<any> {
    return this.apiService
      .apiCall("GET", "document-archives/export-historical-docs", {}, { responseType: "blob" })
      .pipe(
        catchError(err => {
          return this.helperService.parseErrorBlob(err);
        })
      );
  }

  /**
   * Download every file of one archive row as a single zip, built by the back-end.
   *
   * The whole response is observed, not only the body: the zip name travels in
   * `Content-Disposition` and the number of files left out in `X-Archive-Skipped-Files`.
   *
   * Errors are left to the caller and NOT passed through `helperService.parseErrorBlob`: that one
   * drops the HTTP status, which is what the dialog picks its text from, and never settles when
   * the body is not JSON. Note that ApiService emits a failure without status (network down,
   * connection cut once the zip has started) as an `Error` VALUE, not as an error.
   *
   * @since 3.0.0
   *
   * @param documentArchiveId archive of the property, the id of GET document-archives/:id
   * @param docId `_id` of the row as GET document-archives/:id returns it (`docId` on the page rows)
   */
  downloadDocFilesZip(
    documentArchiveId: string,
    docId: string
  ): Observable<HttpResponse<Blob> | Error> {
    return this.apiService.apiCall(
      "GET",
      `document-archives/${encodeURIComponent(documentArchiveId)}/docs/${encodeURIComponent(docId)}/files/zip`,
      undefined,
      { responseType: "blob", observe: "response" },
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get archive data from given path
   *
   * @since 1.0.0
   */
  getByFullPath(path: string): Observable<any> {
    return this.apiService
      .apiCall("GET", path)
      .pipe(
        catchError(err => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Update condo structure with given documentArchiveId with given data
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of condo structure to update
   * @param data New condo structure's data
   */
  update(documentArchiveId: string, data: object): Observable<any> {
    return this.apiService.apiCall(
      "PUT",
      `document-archives/${documentArchiveId}`,
      data
    );
  }

  /**
   * Delete permanently a condo structure
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of the condo structure
   */
  delete(documentArchiveId: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `document-archives/${documentArchiveId}`
    );
  }

  /**
   * Update condo structure section
   *
   * @since 1.0.0
   *
   * @param endpoint Section raw endpoint
   * @param documentArchiveId ID of condo structure to update
   * @param sectionId ID of condo structure section to update
   * @param data New condo structure's data
   */
  updateSection(
    endpoint: string,
    documentArchiveId: string,
    sectionId: string,
    data: object
  ): Observable<any> {
    const endpoint2 = endpoint.replace(":structureId", documentArchiveId);
    return this.apiService.apiCall("PUT", `${endpoint2}/${sectionId}`, data);
  }

  /**
   * Create condo structure section item
   *
   * @since 1.0.0
   *
   * @param endpoint Section raw endpoint
   * @param documentArchiveId ID of condo structure where to create section item
   * @param data New condo structure item's data
   */
  createSectionItem(
    endpoint: string,
    documentArchiveId: string,
    data: object
  ): Observable<any> {
    const endpoint2 = endpoint.replace(":structureId", documentArchiveId);
    return this.apiService.apiCall("POST", endpoint2, data);
  }

  /**
   * This gets all the contracts of the property even the canceled and expired ones
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of the documentArchive to get
   */
  getContracts(documentArchiveId: string): Observable<any> {
    return this.apiService
      .apiCall("GET", `document-archives/${documentArchiveId}/contracts`)
      .pipe(
        catchError(err => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * This gets all documents in the archive that have the same barcode
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of the documentArchive 
   * @param barCode Barcode
   */
  getDocsByBarcode(documentArchiveId: string, barCode: string): Observable<any> {
    return this.apiService
      .apiCall("GET", `document-archives/${documentArchiveId}/by-barcode/${barCode}`)
      .pipe(
        catchError(err => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Create a new "intervento di archiviazione" on the given document archive.
   *
   * The list is archive-wide: the created name becomes selectable in every
   * subarea that groups documents by intervention ("Cause legali",
   * "Interventi successivi", "Pratiche edilizie").
   *
   * The response returns the name as it has been persisted (trimmed by the
   * BE): callers must refresh their local list with `res.data.name` and not
   * with the submitted value.
   *
   * Errors are NOT handled here on purpose: the caller renders them inline.
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of the document archive
   * @param name Name of the intervention to create
   * @returns Observable<any>
   */
  createIntervention(documentArchiveId: string, name: string): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      `document-archives/${documentArchiveId}/interventions`,
      { name },
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Rename an "intervento di archiviazione" and cascade the new name on every
   * linked document. The response returns `data.docsUpdated`.
   *
   * IMPORTANT: `from` must be passed exactly as it comes from the archive
   * (`nameInterventionArchiving`), without any trim. The BE looks it up with
   * an exact match and historical names may keep trailing spaces: trimming
   * `from` makes those records unreachable and answers 404.
   *
   * Errors are NOT handled here on purpose: the caller renders them inline.
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of the document archive
   * @param from Current name, raw (never trimmed)
   * @param to New name, already trimmed by the caller
   * @returns Observable<any>
   */
  renameIntervention(
    documentArchiveId: string,
    from: string,
    to: string
  ): Observable<any> {
    return this.apiService.apiCall(
      "PATCH",
      `document-archives/${documentArchiveId}/interventions`,
      { from, to },
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Delete an "intervento di archiviazione". It is rejected with a 409 when
   * documents are still linked to it (deleted documents included).
   *
   * The name travels in the request BODY - not in the URL nor in the query
   * string - because it can contain "/", spaces and accents. `ApiService`
   * puts the 3rd argument in the query string on DELETE, so the payload is
   * passed through the extra options instead (Angular's HttpClient forwards
   * `options.body` to the underlying request for every verb).
   *
   * Errors are NOT handled here on purpose: the caller renders them inline.
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of the document archive
   * @param name Name to delete, raw (never trimmed)
   * @returns Observable<any>
   */
  deleteIntervention(documentArchiveId: string, name: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `document-archives/${documentArchiveId}/interventions`,
      undefined,
      { body: { name } },
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Create a new "conduttore" on the given document archive.
   *
   * The V2 documents router is mounted on `document-archives`, the same base
   * used by every other archive endpoint here. An earlier version of the API
   * contract announced a `documents/...` prefix: that mount does not exist and
   * answers the router catch-all ("Endpoint non trovato").
   *
   * The list is archive-wide and the created name becomes selectable right
   * away, but the conductor shows up as a tree section only once at least one
   * document uses it.
   *
   * The response returns the persisted name (uppercased and trimmed by the BE):
   * callers must refresh their local list with `res.data.name`.
   *
   * Errors are NOT handled here on purpose: the caller renders them inline.
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of the document archive
   * @param name Name of the conductor to create
   * @returns Observable<any>
   */
  createConductor(documentArchiveId: string, name: string): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      `document-archives/${documentArchiveId}/conductors`,
      { name },
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Rename a "conduttore" and cascade the new name on every linked document,
   * deleted and expired ones included. The response returns
   * `data.docsUpdated`, which can legitimately be 0 when the name only existed
   * in the selection list.
   *
   * IMPORTANT: `from` must be passed exactly as it comes from the archive
   * (`conductorName`), without any trim. The BE matches it ignoring case but
   * RESPECTING spaces, and historical names may keep trailing spaces: trimming
   * `from` makes those records unreachable.
   *
   * Errors are NOT handled here on purpose: the caller renders them inline.
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of the document archive
   * @param from Current name, raw (never trimmed)
   * @param to New name, already normalized by the caller
   * @returns Observable<any>
   */
  renameConductor(
    documentArchiveId: string,
    from: string,
    to: string
  ): Observable<any> {
    return this.apiService.apiCall(
      "PATCH",
      `document-archives/${documentArchiveId}/conductors`,
      { from, to },
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Delete a "conduttore". It is rejected with a 409 when any document is
   * linked to it, in any state: deleted, expired and mockups count too. Since
   * documents are never physically removed, a conductor used at least once can
   * only be renamed from then on. Idempotent (200) on a name that is not in
   * the list.
   *
   * The name travels in the request BODY - not in the URL nor in the query
   * string - because it can contain "/", spaces and accents. `ApiService` puts
   * the 3rd argument in the query string on DELETE, so the payload goes
   * through the extra options instead (Angular's HttpClient forwards
   * `options.body` to the underlying request for every verb).
   *
   * Errors are NOT handled here on purpose: the caller renders them inline.
   *
   * @since 1.0.0
   *
   * @param documentArchiveId ID of the document archive
   * @param name Name to delete, raw (never trimmed)
   * @returns Observable<any>
   */
  deleteConductor(documentArchiveId: string, name: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `document-archives/${documentArchiveId}/conductors`,
      undefined,
      { body: { name } },
      undefined,
      undefined,
      "v2"
    );
  }
}
